"use client";

import { LocaleLink, useLocalePathname } from "@i18n/routing";
import {
	isSolutionDemoHashHref,
	solutionDemoEarlyPreloadIntentHandlers,
} from "@marketing/home/lib/solution-demo-early-preload";
import { useMarketingHomeScrollPending } from "@marketing/shared/components/marketing-home-scroll-pending-context";
import { MarketingLandingVideoNavChips } from "@marketing/shared/components/marketing-landing-video-nav-chips";
import { useMarketingLandingVideoOverlay } from "@marketing/shared/components/marketing-landing-video-overlay-context";
import { useMarketingNavbarLinksOptional } from "@marketing/shared/components/marketing-navbar-links-context";
import { useMarketingHomeScrollSpy } from "@marketing/shared/hooks/use-marketing-home-scroll-spy";
import { useScrollNearTop } from "@marketing/shared/hooks/use-scroll-near-top";
import { useMarketingLandingHashNavigation } from "@marketing/shared/lib/marketing-landing-hash-navigation";
import { marketingNavSectionIdFromHashHref } from "@marketing/shared/lib/marketing-landing-home-sections";
import { scrollToSmoothRespectingMotion } from "@shared/lib/scroll-to-smooth-respecting-motion";
import {
	MARKETING_NAV_DROPDOWN_CONTENT_Z_CLASS,
	MARKETING_NAVBAR_BELOW_BANNER_Z_CLASS,
	MARKETING_PRICING_OVERLAY_LOCKED_NAV_Z_CLASS,
} from "@marketing/shared/lib/marketing-layout-vars";
import { getMarketingLinkPrefetch } from "@marketing/shared/lib/marketing-route-prefetch";
import {
	marketingSheetContentChromeClassName,
	marketingSheetUnderHeaderOverlayClassName,
} from "@marketing/shared/lib/marketing-sheet-chrome";
import {
	HeaderNavMenu,
	type HeaderNavMenuItem,
} from "@repo/ui/components/custom/header-nav-menu";
import { DropdownMenuItem } from "@repo/ui/components/dropdown-menu";
import { Sheet, SheetContent, SheetTitle } from "@repo/ui/components/sheet";
import { cn } from "@repo/ui/lib/cn";
import { useSession } from "@saas/auth/hooks/use-session";
import { AuthSurfaceSignOutButton } from "@saas/shared/components/AuthSurfaceSignOutButton";
import { Logo } from "@saas/shared/components/Logo";
import { ColorModeToggle } from "@shared/components/ColorModeToggle";
import { CursorStyleToggle } from "@shared/components/CursorStyleToggle";
import { ClientFeatureErrorBoundary } from "@shared/components/client-feature-error-boundary";
import { LocaleSwitch } from "@shared/components/LocaleSwitch";
import { BANNER_DISMISS_ICON_BUTTON_CLASS } from "@shared/lib/chrome/banner-dismiss-chrome";
import {
	MarketingPrimaryCta as PrimaryCta,
	MarketingSecondaryCta as SecondaryCta,
} from "@shared/lib/cta/marketing-cta";
import { replaceStateAndNotify } from "@shared/lib/history/dispatch-url-change";
import { Lock, MenuIcon, XIcon } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import NextLink from "next/link";
import { useTranslations } from "next-intl";
import {
	type MouseEvent,
	Suspense,
	type SyntheticEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useMediaQuery } from "usehooks-ts";
import { config } from "@/config";

const NAV_ACTIVE_BG_SPRING = {
	type: "spring" as const,
	stiffness: 420,
	damping: 28,
	mass: 0.82,
};

/** Matches legacy `-inset-x-2` pill overflow vs the measured link box. */
const DESKTOP_NAV_PILL_HORIZONTAL_OUTSET_PX = 8;

/** Same width trick as SaaS entity tab dropdowns (`longest` ghost + visible label). */
function longestMarketingNavLabel(labels: readonly string[]): string {
	if (labels.length === 0) {
		return "";
	}
	return labels.reduce((a, b) => (a.length >= b.length ? a : b));
}

/** Visual match for the desktop active nav pill + chevron (lg–xl dropdown trigger). */
const MARKETING_HOME_NAV_DROPDOWN_TRIGGER_CLASS = cn(
	"inline-flex w-max max-w-full min-w-0 items-center gap-2 rounded-md",
	"bg-indigo-300 px-3 py-2 text-left font-extrabold text-sm text-slate-950 shadow-none",
	"ring-1 ring-indigo-500/25 ring-inset",
	"dark:bg-[#363a4c] dark:text-neutral-50 dark:ring-1 dark:ring-[#464b5e] dark:ring-inset",
	"hover:bg-indigo-200 hover:text-slate-950 dark:hover:bg-[#3d4256] dark:hover:text-neutral-50",
	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
);

function MarketingNavLogoMark({
	nearTop,
	forceIconOnlyLogo,
}: {
	nearTop: boolean;
	forceIconOnlyLogo: boolean;
}) {
	return (
		<div className="relative flex h-10 shrink-0 items-center">
			<span
				className={cn(
					"relative z-[1] flex h-10 shrink-0 items-center",
					forceIconOnlyLogo
						? "hidden"
						: cn(
								nearTop ? "max-lg:flex" : "max-lg:hidden",
								"lg:flex",
							),
				)}
			>
				<Logo disableLink />
			</span>
			<span
				className={cn(
					"flex h-10 shrink-0 items-center justify-start",
					forceIconOnlyLogo
						? "relative z-[1] opacity-100"
						: cn(
								"lg:pointer-events-none lg:absolute lg:inset-0 lg:z-0 lg:opacity-0",
								nearTop
									? "max-lg:pointer-events-none max-lg:absolute max-lg:inset-0 max-lg:z-0 max-lg:opacity-0"
									: "max-lg:pointer-events-auto max-lg:relative max-lg:z-[1] max-lg:opacity-100",
							),
				)}
			>
				<Logo disableLink iconOnly />
			</span>
		</div>
	);
}

function MarketingNavLogoLink({
	nearTop,
	landingVideoOpen,
	onLogoClick,
	homeAriaLabel,
}: {
	nearTop: boolean;
	/** Landing hero video overlay open — match compact (icon-only) logo at all breakpoints. */
	landingVideoOpen: boolean;
	onLogoClick: (e: MouseEvent<HTMLAnchorElement>) => void;
	homeAriaLabel: string;
}) {
	/** Icon-only at all breakpoints while the landing video overlay is open. */
	const forceIconOnlyLogo = landingVideoOpen;

	return (
		<LocaleLink
			href="/"
			prefetch={getMarketingLinkPrefetch("/")}
			className="block shrink-0 cursor-pointer rounded-md outline-none hover:no-underline focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:no-underline"
			aria-label={homeAriaLabel}
			onClick={onLogoClick}
		>
			<MarketingNavLogoMark
				nearTop={nearTop}
				forceIconOnlyLogo={forceIconOnlyLogo}
			/>
		</LocaleLink>
	);
}

function HiddenMarketingLocaleSwitch() {
	return (
		<span
			className="hidden size-9 shrink-0 cursor-default items-center justify-center overflow-hidden opacity-0 pointer-events-none select-none lg:inline-flex"
			aria-hidden="true"
		>
			<Suspense fallback={<span className="block size-9" aria-hidden />}>
				<LocaleSwitch />
			</Suspense>
		</span>
	);
}

interface DesktopNavPillGeometry {
	left: number;
	width: number;
	top: number;
	height: number;
}

function measureDesktopNavActivePill(
	track: HTMLElement,
	sectionId: string,
): DesktopNavPillGeometry | null {
	const anchor = track.querySelector<HTMLElement>(
		`[data-marketing-home-nav-anchor="${sectionId}"]`,
	);
	if (!anchor) {
		return null;
	}
	const trackRect = track.getBoundingClientRect();
	const anchorRect = anchor.getBoundingClientRect();
	return {
		left: anchorRect.left - trackRect.left + track.scrollLeft,
		width: anchorRect.width,
		top: anchorRect.top - trackRect.top + track.scrollTop,
		height: anchorRect.height,
	};
}

interface NavBarProps {
	/** When true, the parent `<header>` is already `sticky`; avoid nested sticky nav. */
	underStickyMarketingHeader?: boolean;
	/**
	 * `overlay-locked` — pricing overlays (logo no link, ColorMode, close X).
	 * `waitlist-locked` — force waitlist chrome; otherwise pathname `/waitlist` still applies.
	 */
	variant?: "default" | "waitlist-locked" | "overlay-locked";
	/** overlay-locked: dismiss handler for the close control. */
	onClose?: () => void;
	/** overlay-locked: accessible label for the close control. */
	closeLabel?: string;
}

export function NavBar({
	underStickyMarketingHeader = false,
	variant = "default",
	onClose,
	closeLabel,
}: NavBarProps) {
	const t = useTranslations();
	const { user } = useSession();
	const landingVideoOverlay = useMarketingLandingVideoOverlay();
	const marketingNavbarLinks = useMarketingNavbarLinksOptional();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const localePathname = useLocalePathname();
	const nearTop = useScrollNearTop();
	const prefersReducedMotion = useReducedMotion();
	const activeLandingSectionId = useMarketingHomeScrollSpy();
	const { pendingLandingSectionId, setPendingLandingSectionId } =
		useMarketingHomeScrollPending();

	/**
	 * Mobile sheet still uses `layoutId` between rows (slow interaction surface).
	 * Desktop uses a single measured pill — shared `layoutId` projected duplicates during
	 * fast scroll spy updates (e.g. flinging back to hero).
	 */
	const navActiveBgPresenceProps = {
		initial: { opacity: 1 },
		animate: { opacity: 1 },
		exit: { opacity: 1 },
	} as const;

	const navActiveBgMotionTransition =
		prefersReducedMotion === true
			? ({ duration: 0.18, ease: "easeOut" } as const)
			: ({ layout: NAV_ACTIVE_BG_SPRING } as const);

	const handleMobileMenuClose = useCallback(() => {
		setMobileMenuOpen(false);
	}, []);

	const { onHashHrefClick } = useMarketingLandingHashNavigation({
		onAfterNavigate: handleMobileMenuClose,
	});

	// Intentional deps: close mobile menu when locale route changes; `handleMobileMenuClose` is stable via `useCallback`
	useEffect(() => {
		handleMobileMenuClose();
	}, [localePathname]);

	useEffect(() => {
		if (localePathname !== "/") {
			setPendingLandingSectionId(null);
		}
	}, [localePathname, setPendingLandingSectionId]);

	useEffect(() => {
		if (
			pendingLandingSectionId != null &&
			activeLandingSectionId === pendingLandingSectionId
		) {
			setPendingLandingSectionId(null);
		}
	}, [
		activeLandingSectionId,
		pendingLandingSectionId,
		setPendingLandingSectionId,
	]);

	useEffect(() => {
		const clearPendingOnHistoryNav = () => {
			setPendingLandingSectionId(null);
		};
		window.addEventListener("popstate", clearPendingOnHistoryNav);
		return () =>
			window.removeEventListener("popstate", clearPendingOnHistoryNav);
	}, [setPendingLandingSectionId]);

	const handleLogoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		if (localePathname !== "/") {
			return;
		}
		e.preventDefault();
		setPendingLandingSectionId(null);
		scrollToSmoothRespectingMotion({ top: 0, behavior: "smooth" });
		replaceStateAndNotify(
			`${window.location.pathname}${window.location.search}`,
			{
				reason: "navigation",
				source: "marketing-navbar-logo",
			},
		);
	};

	const handleSmoothScroll = (href: string) => (e: SyntheticEvent) => {
		onHashHrefClick(e, href);
	};

	const solutionDemoPreloadIntent = solutionDemoEarlyPreloadIntentHandlers();

	const solutionDemoIntentProps = (href: string) =>
		isSolutionDemoHashHref(href) ? solutionDemoPreloadIntent : undefined;

	const menuItems = useMemo(
		() => [
			{ label: t("common.menu.problems"), href: "/#problems" },
			{ label: t("common.menu.mission"), href: "/#mission" },
			{
				label: t("common.menu.solution"),
				href: "/#solution",
			},
			{ label: t("common.menu.features"), href: "/#features" },
			{ label: t("common.menu.testimonials"), href: "/#testimonials" },
			{ label: t("common.menu.earlyAccess"), href: "/#earlyaccess" },
			{ label: t("common.menu.pricing"), href: "/pricing" },
			{ label: t("common.menu.faq"), href: "/#faqs" },
		],
		[t],
	);

	const headerNavMenuItems = useMemo(
		() =>
			menuItems.map((m) => ({
				id: marketingNavSectionIdFromHashHref(m.href) ?? m.href,
				label: m.label,
				href: m.href,
			})),
		[menuItems],
	);

	const alternateNavItems = marketingNavbarLinks?.alternateNavItems ?? null;
	const forceCollapseNavDropdown =
		marketingNavbarLinks?.forceCollapseNavDropdown ?? false;
	const navLinksDisabled = marketingNavbarLinks?.navLinksDisabled ?? false;
	const alternateNavActiveItemId =
		marketingNavbarLinks?.alternateNavActiveItemId ?? null;
	const onAlternateNavItemActivate =
		marketingNavbarLinks?.onAlternateNavItemActivate ?? null;

	const centerMenuItems: readonly HeaderNavMenuItem[] =
		alternateNavItems ?? headerNavMenuItems;

	const highlightedLandingSectionId =
		pendingLandingSectionId ?? activeLandingSectionId;

	const isPricingPath =
		localePathname === "/pricing" || localePathname.endsWith("/pricing");

	const highlightedCenterNavId =
		alternateNavItems != null
			? alternateNavActiveItemId
			: isPricingPath
				? "/pricing"
				: highlightedLandingSectionId;

	const isLandingNavItemActive = (href: string) => {
		if (href === "/pricing") {
			return isPricingPath;
		}
		const sectionId = marketingNavSectionIdFromHashHref(href);
		return (
			sectionId != null &&
			highlightedLandingSectionId != null &&
			sectionId === highlightedLandingSectionId
		);
	};

	const isXlNavInline = useMediaQuery("(min-width: 1280px)");

	const longestNavDropdownSizingLabel = useMemo(() => {
		const atTop = t("common.menu.marketingLandingNavAtTop");
		if (alternateNavItems != null && alternateNavItems.length > 0) {
			return longestMarketingNavLabel(
				alternateNavItems.map((m) => m.label),
			);
		}
		const labels = menuItems.map((m) => m.label);
		return longestMarketingNavLabel([...labels, atTop]);
	}, [alternateNavItems, menuItems, t]);

	const activeNavDropdownLabel = useMemo(() => {
		const atTop = t("common.menu.marketingLandingNavAtTop");
		if (alternateNavItems != null && alternateNavItems.length > 0) {
			const found = alternateNavItems.find(
				(m) => m.id === alternateNavActiveItemId,
			);
			return found?.label ?? alternateNavItems[0]?.label ?? atTop;
		}
		if (isPricingPath) {
			return t("common.menu.pricing");
		}
		if (highlightedLandingSectionId == null) {
			return atTop;
		}
		const found = menuItems.find(
			(m) =>
				marketingNavSectionIdFromHashHref(m.href) ===
				highlightedLandingSectionId,
		);
		return found?.label ?? atTop;
	}, [
		alternateNavActiveItemId,
		alternateNavItems,
		highlightedLandingSectionId,
		isPricingPath,
		menuItems,
		t,
	]);

	const desktopNavTrackRef = useRef<HTMLDivElement>(null);
	const lastDesktopNavPillGeomRef = useRef<DesktopNavPillGeometry | null>(
		null,
	);
	const [desktopNavPillGeom, setDesktopNavPillGeom] =
		useState<DesktopNavPillGeometry | null>(null);

	const syncDesktopNavActivePill = useCallback(() => {
		const track = desktopNavTrackRef.current;
		if (
			track == null ||
			localePathname !== "/" ||
			!isXlNavInline ||
			alternateNavItems != null
		) {
			setDesktopNavPillGeom(null);
			return;
		}
		if (highlightedLandingSectionId == null) {
			setDesktopNavPillGeom(null);
			return;
		}
		const measured = measureDesktopNavActivePill(
			track,
			highlightedLandingSectionId,
		);
		if (measured != null) {
			lastDesktopNavPillGeomRef.current = measured;
			setDesktopNavPillGeom(measured);
		}
	}, [
		alternateNavItems,
		localePathname,
		highlightedLandingSectionId,
		isXlNavInline,
	]);

	useLayoutEffect(() => {
		syncDesktopNavActivePill();
	}, [syncDesktopNavActivePill]);

	useLayoutEffect(() => {
		const track = desktopNavTrackRef.current;
		if (track == null) {
			return;
		}
		const ro = new ResizeObserver(() => {
			syncDesktopNavActivePill();
		});
		ro.observe(track);
		window.addEventListener("resize", syncDesktopNavActivePill);
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", syncDesktopNavActivePill);
		};
	}, [syncDesktopNavActivePill]);

	const DESKTOP_NAV_PILL_VISIBILITY_SPRING = {
		type: "spring" as const,
		stiffness: 520,
		damping: 34,
		mass: 0.68,
	};

	const desktopNavActivePillTransition =
		prefersReducedMotion === true
			? ({ duration: 0.18, ease: "easeOut" } as const)
			: ({
					left: NAV_ACTIVE_BG_SPRING,
					top: NAV_ACTIVE_BG_SPRING,
					width: NAV_ACTIVE_BG_SPRING,
					height: NAV_ACTIVE_BG_SPRING,
					opacity: { duration: 0.18, ease: "easeOut" },
					scale: DESKTOP_NAV_PILL_VISIBILITY_SPRING,
				} as const);

	const desktopNavPillGeomForMotion =
		desktopNavPillGeom ?? lastDesktopNavPillGeomRef.current;

	const desktopNavActivePillVisible =
		alternateNavItems == null &&
		localePathname === "/" &&
		highlightedLandingSectionId != null &&
		desktopNavPillGeomForMotion != null;

	const desktopNavPillMotionAnimate =
		desktopNavPillGeomForMotion != null
			? {
					opacity: desktopNavActivePillVisible ? 1 : 0,
					scale:
						desktopNavActivePillVisible ||
						prefersReducedMotion === true
							? 1
							: 0.94,
					left:
						desktopNavPillGeomForMotion.left -
						DESKTOP_NAV_PILL_HORIZONTAL_OUTSET_PX,
					width:
						desktopNavPillGeomForMotion.width +
						DESKTOP_NAV_PILL_HORIZONTAL_OUTSET_PX * 2,
					top: desktopNavPillGeomForMotion.top,
					height: desktopNavPillGeomForMotion.height,
				}
			: {
					opacity: 0,
					scale: prefersReducedMotion === true ? 1 : 0.94,
					left: 0,
					width: 0,
					top: 0,
					height: 0,
				};

	const showLandingVideoNavChips =
		localePathname === "/" &&
		landingVideoOverlay.isOpen &&
		!landingVideoOverlay.isFullscreen;

	useEffect(() => {
		if (landingVideoOverlay.isOpen) {
			setMobileMenuOpen(false);
		}
	}, [landingVideoOverlay.isOpen]);

	const isOverlayLockedNav = variant === "overlay-locked";
	const isWaitlistLockedNav =
		variant === "waitlist-locked" || localePathname === "/waitlist";

	if (isOverlayLockedNav) {
		const resolvedCloseLabel =
			closeLabel ?? t("common.menu.closeMobileMenu");

		return (
			<nav
				aria-label={t("common.menu.siteNavigation")}
				className={cn(
					"select-none",
					"w-full transition-[box-shadow,background-color,backdrop-filter] duration-200",
					"border-b border-border/40 bg-background/65 backdrop-blur-md backdrop-saturate-150 supports-[backdrop-filter]:bg-background/50",
					"shadow-[0_1px_0_hsl(var(--border)_/_0.35)]",
					MARKETING_PRICING_OVERLAY_LOCKED_NAV_Z_CLASS,
					"sticky top-0",
				)}
				data-test="navigation"
				data-marketing-navbar-variant="overlay-locked"
			>
				<div className="w-full px-6">
					<div
						className={cn(
							"grid w-full max-w-full grid-cols-[1fr_auto_1fr] items-center gap-x-4 transition-[padding] duration-200 sm:gap-x-5 lg:gap-x-6",
							!nearTop ? "py-4" : "py-6",
						)}
					>
						<div className="flex min-w-0 items-center justify-self-start gap-3 lg:gap-4">
							<span
								className="block shrink-0"
								data-marketing-navbar-locked-logo
							>
								<MarketingNavLogoMark
									nearTop={nearTop}
									forceIconOnlyLogo={false}
								/>
							</span>
							<div className="inline-flex min-w-max shrink-0 items-center gap-1.5">
								<span className="inline-flex min-w-8 shrink-0 empty:hidden">
									<ColorModeToggle />
								</span>
							</div>
							<HiddenMarketingLocaleSwitch />
						</div>
						<div className="justify-self-center" aria-hidden />
						<div className="flex items-center justify-self-end">
							<button
								type="button"
								className={cn(
									BANNER_DISMISS_ICON_BUTTON_CLASS,
									"size-9 shrink-0",
									"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
								)}
								aria-label={resolvedCloseLabel}
								data-marketing-navbar-overlay-close
								onClick={() => onClose?.()}
							>
								<XIcon className="size-4" aria-hidden />
							</button>
						</div>
					</div>
				</div>
			</nav>
		);
	}

	if (isWaitlistLockedNav) {
		return (
			<nav
				aria-label={t("common.menu.siteNavigation")}
				className={cn(
					"select-none",
					"w-full transition-[box-shadow,background-color,backdrop-filter] duration-200",
					"border-b border-border/40 bg-background/65 backdrop-blur-md backdrop-saturate-150 supports-[backdrop-filter]:bg-background/50",
					"shadow-[0_1px_0_hsl(var(--border)_/_0.35)]",
					underStickyMarketingHeader
						? cn(MARKETING_NAVBAR_BELOW_BANNER_Z_CLASS, "-mt-px")
						: "sticky top-0 z-50",
				)}
				data-test="navigation"
				data-marketing-navbar-variant="waitlist-locked"
			>
				<div className="w-full px-6">
					<div
						className={cn(
							"flex items-center justify-center gap-4 transition-[padding] duration-200 lg:gap-6",
							!nearTop ? "py-4" : "py-6",
						)}
					>
						<div className="flex items-center justify-center gap-3 lg:gap-4">
							<MarketingNavLogoLink
								nearTop={nearTop}
								landingVideoOpen={landingVideoOverlay.isOpen}
								onLogoClick={handleLogoClick}
								homeAriaLabel={t("common.menu.home")}
							/>
							<div className="inline-flex min-w-max shrink-0 items-center gap-1.5">
								<span className="inline-flex min-w-8 shrink-0 empty:hidden">
									<ColorModeToggle />
								</span>
								<span className="inline-flex min-w-8 shrink-0 empty:hidden">
									<CursorStyleToggle />
								</span>
							</div>
							<HiddenMarketingLocaleSwitch />
						</div>
					</div>
				</div>
			</nav>
		);
	}

	return (
		<nav
			aria-label={t("common.menu.siteNavigation")}
			className={cn(
				"select-none",
				"w-full transition-[box-shadow,background-color,backdrop-filter] duration-200",
				"border-b border-border/40 bg-background/65 backdrop-blur-md backdrop-saturate-150 supports-[backdrop-filter]:bg-background/50",
				"shadow-[0_1px_0_hsl(var(--border)_/_0.35)]",
				underStickyMarketingHeader
					? cn(MARKETING_NAVBAR_BELOW_BANNER_Z_CLASS, "-mt-px")
					: "sticky top-0 z-50",
			)}
			data-test="navigation"
		>
			<div className="w-full px-6">
				<div
					className={cn(
						"grid w-full max-w-full grid-cols-[1fr_auto_1fr] items-center gap-x-4 transition-[padding] duration-200 sm:gap-x-5 lg:gap-x-6",
						!nearTop ? "py-4" : "py-6",
					)}
				>
					<div className="flex min-w-0 items-center justify-self-start gap-3 lg:gap-4">
						<MarketingNavLogoLink
							nearTop={nearTop}
							landingVideoOpen={landingVideoOverlay.isOpen}
							onLogoClick={handleLogoClick}
							homeAriaLabel={t("common.menu.home")}
						/>
						<div className="inline-flex min-w-max shrink-0 items-center gap-1.5">
							<span className="inline-flex min-w-8 shrink-0 empty:hidden">
								<ColorModeToggle />
							</span>
							<span className="inline-flex min-w-8 shrink-0 empty:hidden">
								<CursorStyleToggle />
							</span>
						</div>
						<HiddenMarketingLocaleSwitch />
					</div>

					<div className="relative z-0 flex min-w-0 max-w-full items-center justify-center justify-self-center">
						{showLandingVideoNavChips ? (
							<ClientFeatureErrorBoundary
								onError={() => {
									landingVideoOverlay.setChromeFullscreen(
										false,
									);
									landingVideoOverlay.setIsOpen(false);
								}}
							>
								<MarketingLandingVideoNavChips />
							</ClientFeatureErrorBoundary>
						) : (
							<HeaderNavMenu
								mode="flat"
								items={centerMenuItems}
								activeItemId={highlightedCenterNavId}
								forceCollapseToDropdown={
									forceCollapseNavDropdown
								}
								linksDisabled={navLinksDisabled}
								dropdownModal={false}
								horizontalSeparator="dot"
								inlineWrapperClassName="relative z-0 hidden items-center justify-center xl:flex"
								dropdownWrapperClassName="hidden justify-center lg:flex xl:hidden"
								dropdownTriggerClassName={
									MARKETING_HOME_NAV_DROPDOWN_TRIGGER_CLASS
								}
								dropdownAriaLabel={t(
									"common.menu.marketingLandingNavMenu",
								)}
								dropdownMenuContentClassName={
									MARKETING_NAV_DROPDOWN_CONTENT_Z_CLASS
								}
								dropdownSizingLabel={
									longestNavDropdownSizingLabel
								}
								dropdownValueLabel={activeNavDropdownLabel}
								inlineTrackRef={desktopNavTrackRef}
								inlineTrackDecoration={
									<motion.span
										aria-hidden
										className="pointer-events-none absolute z-[1] rounded-md bg-indigo-300 ring-1 ring-indigo-500/25 ring-inset dark:bg-[#363a4c] dark:ring-1 dark:ring-[#464b5e] dark:ring-inset"
										style={{
											transformOrigin: "center center",
										}}
										initial={false}
										animate={desktopNavPillMotionAnimate}
										transition={
											desktopNavActivePillTransition
										}
									/>
								}
								renderInlineItemWrapper={(ctx, children) => {
									if (alternateNavItems != null) {
										return (
											<span className="relative inline-flex max-w-[min(100%,14rem)] items-center sm:max-w-[min(100%,18rem)]">
												{children}
											</span>
										);
									}
									const sectionId =
										marketingNavSectionIdFromHashHref(
											ctx.item.href ?? "",
										);
									const anchorId =
										sectionId ??
										(ctx.item.href === "/pricing"
											? "/pricing"
											: undefined);
									return (
										<span
											className="relative inline-flex items-center"
											data-marketing-home-nav-anchor={
												anchorId
											}
										>
											{children}
										</span>
									);
								}}
								renderDropdownRow={(ctx) => {
									if (ctx.linksDisabled) {
										return (
											<DropdownMenuItem
												disabled
												className="cursor-not-allowed opacity-60"
											>
												{ctx.item.label}
											</DropdownMenuItem>
										);
									}
									if (
										alternateNavItems != null &&
										onAlternateNavItemActivate != null
									) {
										if (ctx.item.disabled) {
											return (
												<DropdownMenuItem
													className="pointer-events-auto cursor-not-allowed gap-2 opacity-70"
													onSelect={(event) => {
														event.preventDefault();
													}}
												>
													<Lock
														className="size-4 shrink-0 opacity-80"
														aria-hidden
													/>
													<span className="min-w-0 font-semibold leading-snug">
														{ctx.item.label}
													</span>
												</DropdownMenuItem>
											);
										}
										return (
											<DropdownMenuItem
												className={cn(
													"max-w-[min(100vw-3rem,28rem)] cursor-pointer whitespace-normal",
													ctx.isActive && "bg-accent",
												)}
												onSelect={() => {
													onAlternateNavItemActivate(
														ctx.item,
														ctx.index,
													);
												}}
											>
												<span className="font-semibold leading-snug">
													{ctx.item.label}
												</span>
											</DropdownMenuItem>
										);
									}
									return (
										<DropdownMenuItem
											asChild
											className="cursor-pointer"
										>
											<LocaleLink
												href={ctx.item.href ?? ""}
												aria-current={
													ctx.isActive
														? "true"
														: undefined
												}
												className={cn(
													"relative z-[3] block w-auto cursor-pointer whitespace-nowrap px-3 py-2 font-extrabold text-sm transition-colors duration-150 hover:no-underline",
													"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
													ctx.isActive
														? "text-slate-950 hover:!text-slate-950 active:!text-slate-950 dark:text-neutral-50 dark:hover:!text-neutral-50 dark:active:!text-neutral-50"
														: "text-foreground/80 hover:!text-slate-950 active:!text-slate-950 dark:hover:!text-neutral-50 dark:active:!text-neutral-50",
												)}
												onClick={handleSmoothScroll(
													ctx.item.href ?? "",
												)}
												prefetch={getMarketingLinkPrefetch(
													ctx.item.href ?? "",
												)}
												{...solutionDemoIntentProps(
													ctx.item.href ?? "",
												)}
											>
												{ctx.item.label}
											</LocaleLink>
										</DropdownMenuItem>
									);
								}}
								renderLink={(ctx) => {
									const {
										item,
										isActive,
										linksDisabled: ld,
										index,
									} = ctx;
									const inlineFocusRing =
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background";
									if (ld) {
										return (
											<span
												className={cn(
													"relative z-[3] block w-auto whitespace-normal px-3 py-2 text-left font-extrabold text-sm",
													"cursor-not-allowed text-foreground/45",
												)}
											>
												{item.label}
											</span>
										);
									}
									if (
										alternateNavItems != null &&
										onAlternateNavItemActivate != null
									) {
										if (item.disabled) {
											return (
												<span
													className={cn(
														"relative z-[3] flex w-full min-w-0 cursor-not-allowed items-center gap-2 whitespace-normal px-3 py-2 text-left font-extrabold text-sm text-foreground/45",
													)}
													aria-disabled
												>
													<Lock
														className="size-4 shrink-0 opacity-70"
														aria-hidden
													/>
													<span className="min-w-0">
														{item.label}
													</span>
												</span>
											);
										}
										return (
											<button
												type="button"
												className={cn(
													"relative z-[3] block w-full min-w-0 cursor-pointer whitespace-normal px-3 py-2 text-left font-extrabold text-sm transition-colors duration-150",
													inlineFocusRing,
													isActive
														? "text-slate-950 dark:text-neutral-50"
														: "text-foreground/80 hover:text-slate-950 dark:hover:text-neutral-50",
												)}
												aria-current={
													isActive
														? "true"
														: undefined
												}
												onClick={() =>
													onAlternateNavItemActivate(
														item,
														index,
													)
												}
											>
												{item.label}
											</button>
										);
									}
									return (
										<LocaleLink
											href={item.href ?? ""}
											aria-current={
												isActive ? "true" : undefined
											}
											className={cn(
												"relative z-[3] block w-auto cursor-pointer whitespace-nowrap px-3 py-2 font-extrabold text-sm transition-colors duration-150 hover:no-underline",
												inlineFocusRing,
												isActive
													? "text-slate-950 hover:!text-slate-950 active:!text-slate-950 dark:text-neutral-50 dark:hover:!text-neutral-50 dark:active:!text-neutral-50"
													: "text-foreground/80 hover:!text-slate-950 active:!text-slate-950 dark:hover:!text-neutral-50 dark:active:!text-neutral-50",
											)}
											onClick={handleSmoothScroll(
												item.href ?? "",
											)}
											prefetch={getMarketingLinkPrefetch(
												item.href ?? "",
											)}
											{...solutionDemoIntentProps(
												item.href ?? "",
											)}
										>
											{item.label}
										</LocaleLink>
									);
								}}
							/>
						)}
					</div>

					<div className="flex min-w-0 items-center justify-self-end gap-2 lg:gap-3">
						<span
							className={cn(
								"shrink-0",
								nearTop
									? "hidden sm:inline-flex"
									: "inline-flex",
							)}
						>
							<PrimaryCta
								asChild
								ctaSize="small"
								analyticsLocation="marketing_nav"
								analyticsCta="waitlist"
							>
								<LocaleLink
									href="/waitlist"
									prefetch={getMarketingLinkPrefetch(
										"/waitlist",
									)}
								>
									{t("common.menu.waitlist")}
								</LocaleLink>
							</PrimaryCta>
						</span>

						{config.saas.enabled ? (
							user ? (
								<SecondaryCta
									key="dashboard"
									className="hidden md:flex"
									asChild
									ctaSize="small"
								>
									<NextLink href="/app">
										{t("common.menu.dashboard")}
									</NextLink>
								</SecondaryCta>
							) : (
								<SecondaryCta
									key="login"
									className="hidden md:flex"
									asChild
									ctaSize="small"
									analyticsLocation="marketing_nav"
									analyticsCta="login"
								>
									<NextLink
										href="/auth/login"
										prefetch={getMarketingLinkPrefetch(
											"/auth/login",
										)}
									>
										{t("common.menu.login")}
									</NextLink>
								</SecondaryCta>
							)
						) : null}

						<Sheet
							open={mobileMenuOpen}
							onOpenChange={(open) => setMobileMenuOpen(open)}
						>
							<button
								type="button"
								className={cn(
									BANNER_DISMISS_ICON_BUTTON_CLASS,
									"size-9 shrink-0 transition-[background-color,border-color,color] lg:hidden",
									"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
								)}
								aria-expanded={mobileMenuOpen}
								aria-label={
									mobileMenuOpen
										? t("common.menu.closeMobileMenu")
										: t("common.menu.openMobileMenu")
								}
								onClick={() =>
									setMobileMenuOpen((prev) => !prev)
								}
							>
								{mobileMenuOpen ? (
									<XIcon className="size-4" aria-hidden />
								) : (
									<MenuIcon className="size-4" aria-hidden />
								)}
							</button>
							<SheetContent
								className={marketingSheetContentChromeClassName(
									"right",
									cn("w-[280px] select-none border-l p-0"),
								)}
								side="right"
								hideClose
								overlayClassName={
									marketingSheetUnderHeaderOverlayClassName
								}
							>
								<SheetTitle className="sr-only">
									{t("common.menu.mobileNavSheetTitle")}
								</SheetTitle>
								<div className="flex flex-col items-start justify-center px-6 pt-6 pb-8">
									<PrimaryCta
										className="mb-3 w-full"
										asChild
										ctaSize="medium"
										ctaWidth="full"
										analyticsLocation="marketing_nav"
										analyticsCta="waitlist"
									>
										<LocaleLink
											href="/waitlist"
											prefetch={getMarketingLinkPrefetch(
												"/waitlist",
											)}
											onClick={handleMobileMenuClose}
										>
											{t("common.menu.waitlist")}
										</LocaleLink>
									</PrimaryCta>
									<LocaleLink
										href="/roadmap"
										prefetch={getMarketingLinkPrefetch(
											"/roadmap",
										)}
										onClick={handleMobileMenuClose}
										className="mb-3 block w-full rounded-md border border-border/60 bg-muted/30 px-3 py-2.5 text-center font-semibold text-base text-foreground transition-colors hover:bg-muted/50 hover:no-underline"
									>
										{t("common.menu.roadmap")}
									</LocaleLink>
									{alternateNavItems != null
										? alternateNavItems.map(
												(navItem, navIndex) => {
													const isAltActive =
														alternateNavActiveItemId ===
														navItem.id;
													const isCardLocked =
														Boolean(
															navItem.disabled,
														);
													return (
														<span
															key={navItem.id}
															className="relative z-0 inline-flex w-full items-center"
														>
															<AnimatePresence
																initial={false}
															>
																{isAltActive ? (
																	<motion.span
																		key="marketing-home-nav-sheet-active-bg-alt"
																		layoutId="marketing-home-nav-sheet-active-bg"
																		className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-indigo-500/22 ring-1 ring-indigo-500/25 ring-inset dark:bg-[#363a4c] dark:ring-1 dark:ring-[#464b5e] dark:ring-inset"
																		transition={
																			navActiveBgMotionTransition
																		}
																		{...navActiveBgPresenceProps}
																	/>
																) : null}
															</AnimatePresence>
															<button
																type="button"
																disabled={
																	navLinksDisabled ||
																	isCardLocked
																}
																aria-current={
																	isAltActive
																		? "true"
																		: undefined
																}
																className={cn(
																	"relative z-[3] flex w-full items-start gap-2 px-3 py-2 text-left font-medium text-base whitespace-normal transition-colors duration-150",
																	"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
																	(navLinksDisabled ||
																		isCardLocked) &&
																		"!cursor-not-allowed opacity-60",
																	!navLinksDisabled &&
																		!isCardLocked &&
																		"cursor-pointer",
																	isAltActive
																		? "font-bold text-slate-950 hover:text-slate-950 active:text-slate-950 dark:text-neutral-50 dark:hover:text-neutral-50 dark:active:text-neutral-50"
																		: "text-foreground/80 hover:text-slate-950 active:text-slate-950 dark:hover:text-neutral-50 dark:active:text-neutral-50",
																)}
																onClick={() => {
																	if (
																		navLinksDisabled ||
																		isCardLocked
																	) {
																		return;
																	}
																	onAlternateNavItemActivate?.(
																		navItem,
																		navIndex,
																	);
																	handleMobileMenuClose();
																}}
															>
																{isCardLocked ? (
																	<Lock
																		className="mt-0.5 size-4 shrink-0 opacity-70"
																		aria-hidden
																	/>
																) : null}
																<span className="min-w-0 flex-1">
																	{
																		navItem.label
																	}
																</span>
															</button>
														</span>
													);
												},
											)
										: menuItems.map((menuItem) => (
												<span
													key={menuItem.href}
													className="relative z-0 inline-flex w-full items-center"
												>
													<AnimatePresence
														initial={false}
													>
														{isLandingNavItemActive(
															menuItem.href,
														) ? (
															<motion.span
																key="marketing-home-nav-sheet-active-bg-pill"
																layoutId="marketing-home-nav-sheet-active-bg"
																className="pointer-events-none absolute inset-0 z-[1] rounded-md bg-indigo-500/22 ring-1 ring-indigo-500/25 ring-inset dark:bg-[#363a4c] dark:ring-1 dark:ring-[#464b5e] dark:ring-inset"
																transition={
																	navActiveBgMotionTransition
																}
																{...navActiveBgPresenceProps}
															/>
														) : null}
													</AnimatePresence>
													<LocaleLink
														href={menuItem.href}
														aria-current={
															isLandingNavItemActive(
																menuItem.href,
															)
																? "true"
																: undefined
														}
														aria-disabled={
															navLinksDisabled
																? true
																: undefined
														}
														tabIndex={
															navLinksDisabled
																? -1
																: undefined
														}
														onClick={(e) => {
															if (
																navLinksDisabled
															) {
																e.preventDefault();
																return;
															}
															handleSmoothScroll(
																menuItem.href,
															)(e);
														}}
														{...solutionDemoIntentProps(
															menuItem.href,
														)}
														className={cn(
															"relative z-[3] block w-auto px-3 py-2 font-medium text-base transition-colors duration-150 hover:no-underline",
															navLinksDisabled
																? "cursor-not-allowed whitespace-nowrap opacity-60"
																: "cursor-pointer whitespace-nowrap",
															"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
															isLandingNavItemActive(
																menuItem.href,
															)
																? "font-bold text-slate-950 hover:!text-slate-950 active:!text-slate-950 dark:text-neutral-50 dark:hover:!text-neutral-50 dark:active:!text-neutral-50"
																: "text-foreground/80 hover:!text-slate-950 active:!text-slate-950 dark:hover:!text-neutral-50 dark:active:!text-neutral-50",
														)}
														prefetch={
															navLinksDisabled
																? false
																: getMarketingLinkPrefetch(
																		menuItem.href ??
																			"",
																	)
														}
													>
														{menuItem.label}
													</LocaleLink>
												</span>
											))}

									{user ? (
										config.saas.enabled ? (
											<>
												<SecondaryCta
													key="dashboard-sheet"
													asChild
													ctaSize="medium"
													ctaWidth="full"
													className="mt-2 w-full"
													analyticsLocation="marketing_nav"
													analyticsCta="dashboard"
												>
													<NextLink
														href="/app"
														onClick={
															handleMobileMenuClose
														}
													>
														{t(
															"common.menu.dashboard",
														)}
													</NextLink>
												</SecondaryCta>
												<AuthSurfaceSignOutButton
													key="sign-out-sheet"
													className="mt-2 w-full"
												/>
											</>
										) : null
									) : config.saas.enabled ? (
										<SecondaryCta
											key="login-sheet"
											asChild
											ctaSize="medium"
											ctaWidth="full"
											className="mt-2 w-full"
											analyticsLocation="marketing_nav"
											analyticsCta="login"
										>
											<NextLink
												href="/auth/login"
												onClick={handleMobileMenuClose}
												prefetch={getMarketingLinkPrefetch(
													"/auth/login",
												)}
											>
												{t("common.menu.login")}
											</NextLink>
										</SecondaryCta>
									) : null}
								</div>
							</SheetContent>
						</Sheet>
					</div>
				</div>
			</div>
		</nav>
	);
}
